/**
 * ResetPassword — solicita email de reset (POST /auth/reset-password).
 *
 * Backend responde 202 SIEMPRE (anti-enumeration). Frontend muestra el
 * mismo mensaje exista o no el email — coherente con la misma decisión
 * en el backend.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { AuthLayout } from "@/components/ui/AuthLayout";
import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { requestReset } from "@/api/auth";
import { t, translateError } from "@/i18n/es";
import { validateEmail } from "@/lib/validators";
import { ApiError } from "@/types/api";

export function ResetPassword() {
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState<string | undefined>();
  const [formErr, setFormErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormErr(null);

    const emailErrCode = validateEmail(email);
    setEmailErr(emailErrCode ? translateError(emailErrCode.code) : undefined);
    if (emailErrCode) return;

    setSubmitting(true);
    try {
      await requestReset(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Solo errores de red llegan aquí; backend siempre responde 202.
      const code = err instanceof ApiError ? err.code : "network_error";
      setFormErr(translateError(code));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <AuthLayout
        title={t.auth.resetTitle}
        footer={
          <Link to="/login" className="hover:text-bone">
            {t.auth.backToLogin}
          </Link>
        }
      >
        <p
          role="status"
          className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-4 text-sm text-bone"
        >
          {t.auth.resetEmailSent}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t.auth.resetTitle}
      footer={
        <Link to="/login" className="hover:text-bone">
          {t.auth.backToLogin}
        </Link>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormError message={formErr} />
        <Input
          label={t.auth.emailLabel}
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
          placeholder={t.auth.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailErr}
        />
        <Button type="submit" loading={submitting} className="mt-2">
          {t.auth.requestResetCta}
        </Button>
      </form>
    </AuthLayout>
  );
}
